import { BufferWriter, Writer } from "protobufjs";
// import moment from "moment";

import { JobCareer } from "../../../domain/model/job_career";
import {
  IJobCareerRepository,
  TCreateJobCareerParams,
  TJobCareerWithCustomerID,
  TUpdateJobCareerParams,
} from "../../../domain/repository/job_career_repository";

import { IApiClient } from "../client";
import { api, common, entity } from "../rpc/api";
import { getToken } from "./bind_token";

import JobCareerList = entity.JobCareerList;
import CustomerID = entity.CustomerID;
import CustomerIDList = entity.CompanyIDList;
import JobCareerID = entity.JobCareerID;
import JobCareerWithCustomerID = entity.JobCareerWithCustomerID;
import JobCareerWithCustomerIDList = entity.JobCareerWithCustomerIDList;
import CreateJobCareerRequest = api.CreateJobCareerRequest;
import UpdateJobCareerRequest = api.UpdateJobCareerRequest;

import Empty = common.Empty;

import { FrontDate } from "../../../domain/model/frontDate";
import {
  JobCareerConvertResponse,
  JobCareerConvertResponseList,
  JobCareerConvertResponseListByCustomerIDs,
} from "./job_career_api_convert";

class JobCareerAPI implements IJobCareerRepository {
  // Frontend => BackEnd における型変換
  private static atConvertToYearNumber = (
    frontDate: FrontDate | undefined
  ): number => {
    if (!frontDate) {
      return 0;
    } else {
      return frontDate.year === "" ? 0 : Number(frontDate.year);
    }
  };

  private static atConvertToMonthNumber = (
    frontDate: FrontDate | undefined
  ): number => {
    if (!frontDate) {
      return 0;
    } else {
      return frontDate.month === "" ? 0 : Number(frontDate.month);
    }
  };

  private static atConvertToDayNumber = (
    frontDate: FrontDate | undefined
  ): number => {
    if (!frontDate) {
      return 0;
    } else {
      return frontDate.day === "" ? 0 : Number(frontDate.day);
    }
  };

  constructor(readonly apiClient: IApiClient) {}

  public getAllByCustomer(): Promise<JobCareer[]> {
    return new Promise<JobCareer[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Empty = new Empty();
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.JobCareerService/GetAll",
            token,
            Empty.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: JobCareerList = JobCareerList.decode(binary);
          // BE=>FE 型変換
          const resConverted: JobCareerConvertResponseList =
            JobCareerConvertResponseList.from(res);
          const items: JobCareer[] = resConverted.items.map(
            (item: JobCareerConvertResponse): JobCareer => {
              return JobCareer.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllByClient(customerId: string): Promise<JobCareer[]> {
    return new Promise<JobCareer[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: CustomerID = new CustomerID();
          req.id = customerId;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.JobCareerService/GetAllByCustomer",
            token,
            CustomerID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: JobCareerList = JobCareerList.decode(binary);
          // BE=>FE 型変換
          const resConverted: JobCareerConvertResponseList =
            JobCareerConvertResponseList.from(res);
          const items: JobCareer[] = resConverted.items.map(
            (item: JobCareerConvertResponse): JobCareer => {
              return JobCareer.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllByCustomerIDsForScout(
    ids: string[]
  ): Promise<TJobCareerWithCustomerID[]> {
    return new Promise<TJobCareerWithCustomerID[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: CustomerIDList = new CustomerIDList();
          req.ids = ids;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.JobCareerService/GetAllByCustomerIDsForScout",
            token,
            CustomerIDList.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: JobCareerWithCustomerIDList =
            JobCareerWithCustomerIDList.decode(binary);
          // BE=>FE 型変換
          const items: TJobCareerWithCustomerID[] = res.items.map(
            (item: JobCareerWithCustomerID) => {
              const resConverted: JobCareerConvertResponseListByCustomerIDs =
                JobCareerConvertResponseListByCustomerIDs.from(item);
              const resJobCareers: JobCareer[] = resConverted.items.map(
                (convertItem: JobCareerConvertResponse): JobCareer => {
                  return JobCareer.from(convertItem);
                }
              );
              return {
                jobCareers: resJobCareers,
                customerID: item.customerID,
              };
            }
          );

          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllByCustomerForScout(customerId: string): Promise<JobCareer[]> {
    return new Promise<JobCareer[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: CustomerID = new CustomerID();
          req.id = customerId;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.JobCareerService/GetAllByCustomerForScout",
            token,
            CustomerID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: JobCareerList = JobCareerList.decode(binary);
          // BE=>FE 型変換
          const resConverted: JobCareerConvertResponseList =
            JobCareerConvertResponseList.from(res);
          const items: JobCareer[] = resConverted.items.map(
            (item: JobCareerConvertResponse): JobCareer => {
              return JobCareer.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public create(
    createJobCareerRequest: TCreateJobCareerParams
  ): Promise<JobCareer> {
    return new Promise<JobCareer>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: CreateJobCareerRequest = new CreateJobCareerRequest();
          // Frontend => BackEndにおける型変換
          const startedAtYear_number: number =
            JobCareerAPI.atConvertToYearNumber(
              createJobCareerRequest.startedAt
            );
          const startedAtMonth_number: number =
            JobCareerAPI.atConvertToMonthNumber(
              createJobCareerRequest.startedAt
            );
          const startedAtDay_number: number = JobCareerAPI.atConvertToDayNumber(
            createJobCareerRequest.startedAt
          );
          const endedAtYear_number: number = JobCareerAPI.atConvertToYearNumber(
            createJobCareerRequest.endedAt
          );
          const endedAtMonth_number: number =
            JobCareerAPI.atConvertToMonthNumber(createJobCareerRequest.endedAt);
          const endedAtDay_number: number = JobCareerAPI.atConvertToDayNumber(
            createJobCareerRequest.endedAt
          );
          req.companyName = createJobCareerRequest.companyName;
          req.description = createJobCareerRequest.description;
          req.occupations = createJobCareerRequest.occupations;
          req.startedAtYear = startedAtYear_number;
          req.startedAtMonth = startedAtMonth_number;
          req.startedAtDay = startedAtDay_number;
          req.endedAtYear = endedAtYear_number;
          req.endedAtMonth = endedAtMonth_number;
          req.endedAtDay = endedAtDay_number;
          req.post = createJobCareerRequest.post;
          req.employmentStatus = createJobCareerRequest.employmentStatus;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.JobCareerService/Create",
            token,
            CreateJobCareerRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.JobCareer = entity.JobCareer.decode(binary);
          // BE=>FE 型変換
          const resConverted: JobCareerConvertResponse =
            JobCareerConvertResponse.from(res);
          const item: JobCareer = JobCareer.from(resConverted);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public update(
    updateJobCareerRequest: TUpdateJobCareerParams
  ): Promise<JobCareer> {
    return new Promise<JobCareer>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: UpdateJobCareerRequest = new UpdateJobCareerRequest();
          // Frontend => BackEndにおける型変換
          const startedAtYear_number: number =
            JobCareerAPI.atConvertToYearNumber(
              updateJobCareerRequest.startedAt
            );
          const startedAtMonth_number: number =
            JobCareerAPI.atConvertToMonthNumber(
              updateJobCareerRequest.startedAt
            );
          const startedAtDay_number: number = JobCareerAPI.atConvertToDayNumber(
            updateJobCareerRequest.startedAt
          );
          const endedAtYear_number: number = JobCareerAPI.atConvertToYearNumber(
            updateJobCareerRequest.endedAt
          );
          const endedAtMonth_number: number =
            JobCareerAPI.atConvertToMonthNumber(updateJobCareerRequest.endedAt);
          const endedAtDay_number: number = JobCareerAPI.atConvertToDayNumber(
            updateJobCareerRequest.endedAt
          );
          req.id = updateJobCareerRequest.id;
          req.companyName = updateJobCareerRequest.companyName;
          req.description = updateJobCareerRequest.description;
          req.occupations = updateJobCareerRequest.occupations;
          req.startedAtYear = startedAtYear_number;
          req.startedAtMonth = startedAtMonth_number;
          req.startedAtDay = startedAtDay_number;
          req.endedAtYear = endedAtYear_number;
          req.endedAtMonth = endedAtMonth_number;
          req.endedAtDay = endedAtDay_number;
          req.post = updateJobCareerRequest.post;
          req.employmentStatus = updateJobCareerRequest.employmentStatus;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.JobCareerService/Update",
            token,
            UpdateJobCareerRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.JobCareer = entity.JobCareer.decode(binary);
          // BE=>FE 型変換
          const resConverted: JobCareerConvertResponse =
            JobCareerConvertResponse.from(res);
          const item: JobCareer = JobCareer.from(resConverted);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public delete(jobCareerId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: JobCareerID = new JobCareerID();
          req.id = jobCareerId;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.JobCareerService/Delete",
            token,
            JobCareerID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public deleteAll(customerId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: CustomerID = new CustomerID();
          req.id = customerId;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.JobCareerService/DeleteAll",
            token,
            CustomerID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }
}

const createJobCareerAPI = (apiClient: IApiClient): IJobCareerRepository => {
  return new JobCareerAPI(apiClient);
};

export { createJobCareerAPI };



// WEBPACK FOOTER //
// ./src/infra/api/service/job_career_api.ts